
import { useEffect, useRef } from "react";
import { useLoadingState } from "@/utils/loading";

interface BrowserHistoryHandlerProps {
  onLocationChange?: (path: string) => void;
  prefetchRouteData?: (path: string) => void;
  loadView?: (path: string, options?: any) => Promise<void>;
  cachedViews?: Record<string, any>;
}

/**
 * Component to handle browser back/forward navigation
 * This ensures that the content manager state is properly restored
 * when using browser navigation buttons
 */
const BrowserHistoryHandler = ({ 
  onLocationChange, 
  prefetchRouteData = () => {}, 
  loadView = async () => {}, 
  cachedViews = {} 
}: BrowserHistoryHandlerProps) => {
  const { startLoading, stopLoading, networkStatus } = useLoadingState();
  const lastPathRef = useRef<string>(window.location.pathname);
  
  useEffect(() => {
    // This captures browser back/forward navigation
    const handlePopState = (event: PopStateEvent) => {
      const { pathname } = window.location;
      const state = event.state?.usr || {};
      
      // Track navigation with loading state
      const loadingId = startLoading(`navigation:${pathname}`, 'high');
      
      // Also prefetch any related data for this route
      prefetchRouteData(pathname);
      
      // Load the view with the history state
      loadView(pathname, { state })
        .finally(() => {
          stopLoading(loadingId);
        });
      
      // Call optional callback
      if (onLocationChange) {
        onLocationChange(pathname);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [loadView, onLocationChange, startLoading, stopLoading, prefetchRouteData]);
  
  // Track route changes using the location API
  useEffect(() => {
    const checkPathChange = () => {
      const currentPath = window.location.pathname;
      // Only if path actually changed
      if (currentPath !== lastPathRef.current) {
        lastPathRef.current = currentPath;
        
        // Prefetch related data for this route
        prefetchRouteData(currentPath);
        
        // Determine if this is a first load or a navigation
        const isCached = Boolean(cachedViews[currentPath]);
        const loadingPriority = isCached ? 'normal' : 'high';
        
        // Start a loading state for this navigation
        const loadingId = startLoading(`route:${currentPath}`, loadingPriority);
        
        // Add a timeout to clear the loading state in case something fails
        setTimeout(() => {
          stopLoading(loadingId);
        }, 3000);
      }
    };
    
    // Set up a MutationObserver to detect DOM changes that might indicate navigation
    const observer = new MutationObserver(() => {
      checkPathChange();
    });
    
    // Observe the document body for changes
    observer.observe(document.body, { 
      childList: true,
      subtree: true
    });
    
    // Also check on initial load
    checkPathChange();
    
    return () => observer.disconnect();
  }, [startLoading, stopLoading, cachedViews, prefetchRouteData]);
  
  // Initialize when component mounts
  useEffect(() => {
    // Get any state from the URL (query parameters)
    const searchParams = new URLSearchParams(window.location.search);
    const stateFromUrl: Record<string, any> = {};
    
    searchParams.forEach((value, key) => {
      try {
        // Try to parse as JSON first
        stateFromUrl[key] = JSON.parse(value);
      } catch (e) {
        // If not valid JSON, use the raw value
        stateFromUrl[key] = value;
      }
    });
    
    // Initialize view with state from URL
    if (Object.keys(stateFromUrl).length > 0) {
      loadView(window.location.pathname, { state: stateFromUrl });
    }
    
    // Initial prefetch for the current route and potentially related routes
    prefetchRouteData(window.location.pathname);
    
    // Also prefetch routes that are likely to be visited next
    // Only if we have good network conditions
    if (!networkStatus.isLowBandwidth) {
      // Common paths that users navigate to after the current path
      if (window.location.pathname === '/dashboard') {
        prefetchRouteData('/dashboard/users');
        prefetchRouteData('/dashboard/blog');
      } else if (window.location.pathname === '/blog') {
        // Prefetch blog listing
        prefetchRouteData('/blog');
      }
    }
  }, [loadView, prefetchRouteData, networkStatus.isLowBandwidth]);
  
  // Component doesn't render anything visible
  return null;
};

export default BrowserHistoryHandler;
